/**
 * The instruments a new part can be made from.
 *
 * This is general musical knowledge -- what a clarinet is called, that it is written a tone
 * above what it sounds, that a piano has two staves -- and nothing else. It is deliberately
 * not a theory of orchestration: there is no opinion here about which instruments belong
 * together, what doubles what, or what a good range is, because those are judgements and
 * this is a table of facts an engraver needs to draw the part at all.
 *
 * The ranges are written pitch, not sounding pitch, because that is what appears on the
 * staff the user is looking at. They are the ordinary professional compass of the
 * instrument and are advisory: nothing refuses a note outside them.
 */

import type { Clef } from "../document/types";
import type { Transposition } from "../document/pitch";
import type { PercussionKit } from "../document/percussion";
import type { StringTuning } from "../document/tab";
import { WIND_AND_PERCUSSION } from "./instruments-wind";
import { STRINGS_KEYBOARDS_AND_VOICES } from "./instruments-strings";
import { UNPITCHED_PERCUSSION } from "./kits";

/** Every instrument a part can be made from, in score order. */
export const INSTRUMENTS: readonly InstrumentPreset[] = [
  ...WIND_AND_PERCUSSION,
  ...UNPITCHED_PERCUSSION,
  ...STRINGS_KEYBOARDS_AND_VOICES,
];

/** The families a picker groups its list by. */
export type Family =
  | "woodwind"
  | "brass"
  | "percussion"
  | "keyboard"
  | "plucked"
  | "strings"
  | "voice";

/** One instrument a part can be created as. */
export interface InstrumentPreset {
  /** Stable key, for a caller that stores a choice. */
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string;
  readonly family: Family;
  /** One clef per staff: two for a piano, one for everything else here. */
  readonly clefs: readonly Clef[];
  readonly transposition: Transposition;
  /** MusicXML sound id, which is also how another program recognises the part. */
  readonly sound: string;
  /** General MIDI program, 0-based. */
  readonly midiProgram: number;
  /** The ordinary written compass, as MIDI note numbers. */
  readonly range: { readonly low: number; readonly high: number };
  /**
   * The drums this part is played on, for an unpitched instrument.
   *
   * Absent on everything that has pitches of its own. A part that has one is a percussion
   * staff: what a note means is the drum it names, and the line it sits on is only where
   * that drum is written.
   */
  readonly percussion?: PercussionKit;
  /**
   * The open strings this part is played on, for an instrument written as tablature.
   *
   * Absent on everything else. A part that has one is a tab staff: its lines are strings
   * and the numbers on them are frets -- see `document/tab.ts`.
   */
  readonly tuning?: readonly StringTuning[];
  /** Lines in the staff, when a kit or a tuning is written on other than five. */
  readonly lines?: number;
}

/** One instrument by its id, or `null` when nothing goes by that name. */
export function instrumentBy(id: string): InstrumentPreset | null {
  return INSTRUMENTS.find((instrument) => instrument.id === id) ?? null;
}

/** The instruments of one family, in the order a picker should list them. */
export function familyOf(family: Family): readonly InstrumentPreset[] {
  return INSTRUMENTS.filter((instrument) => instrument.family === family);
}
